import React, { FC, ReactNode, useMemo } from 'react';

import { Flex } from '@adsk/alloy-react-box';
import { Trans } from '@adsk/alloy-react-localization';

import getPluginKey from './helpers/getPluginKey';
import { useTranslation } from './i18n';
import { CommonReferencePickerPlugin } from './types/ReferencePickerPlugin';

interface ReferencePickerEmptyStateProps {
  style?: React.CSSProperties;
  className?: string;
  emptyStateText?: ReactNode;
  plugins: CommonReferencePickerPlugin[];
  allowAddingLinks?: boolean;
}

const getPluginName = (plugin: CommonReferencePickerPlugin) => {
  return <plugin.PluralText lowerCase key={getPluginKey(plugin)} />;
};

const getPlaceholderTranslationKey = (count: number) => {
  if (count === 1) return 'ADD_REFERENCE_PLACEHOLDER_ONE';
  if (count >= 2 && count <= 4) return 'ADD_REFERENCE_PLACEHOLDER_FEW';
  return 'ADD_REFERENCE_PLACEHOLDER_OTHER';
};

const ReferencePickerEmptyState: FC<ReferencePickerEmptyStateProps> = ({
  style,
  className,
  emptyStateText,
  plugins,
  allowAddingLinks,
}) => {
  const { t, i18n } = useTranslation();

  const defaultEmptyStateText = useMemo(() => {
    if (!allowAddingLinks) {
      return t('ADD_REFERENCE_PLACEHOLDER_NO_ADD');
    }
    const options: Record<string, React.ReactElement> = {};
    if (plugins.length === 1) {
      options.name = getPluginName(plugins[0]);
    } else if (plugins.length > 0 && plugins.length <= 4) {
      const firstPartArr = plugins.reduce(
        (acc: React.ReactNode[], p, index) => {
          if (index < plugins.length - 1) {
            acc.push(getPluginName(p));
            if (index < plugins.length - 2) {
              acc.push(', ');
            }
          }
          return acc;
        },
        [],
      );
      options.firstPart = <>{firstPartArr}</>;
      options.lastName = getPluginName(plugins[plugins.length - 1]);
    }

    return (
      <Trans
        i18n={i18n}
        t={t}
        i18nKey={getPlaceholderTranslationKey(plugins.length)}
        components={options}
      />
    );
  }, [t, i18n, plugins, allowAddingLinks]);

  const emptyState = emptyStateText || defaultEmptyStateText;

  return (
    <Flex
      className={className}
      justifyContent="center"
      textAlign="center"
      padding="26px 66px"
      color="charcoal700"
      typography="bodyMedium"
      style={style}
    >
      {emptyState}
    </Flex>
  );
};

export default ReferencePickerEmptyState;
